<template>
  <div v-if="isReadOnly" class="flex flex-col gap-x-2">
    <label v-if="label" :for="fieldName" class="">
      <span class="text-brand-black-secondary">{{ t9nLabel }}</span>
      <HintTippy v-if="hint" :hint="hint"></HintTippy>
    </label>
    <span class="flex-1 text-lg">
      {{ fullOutput?.replace('00', '+') }}
    </span>
  </div>
  <div v-else class="flex flex-col gap-y-1" ref="wrapperDomRef">
    <label class="w-full flex flex-col gap-0.5">
      <div :class="direction" class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
        <div v-if="label" :class="labelClass" class="flex w-1/2 items-center">
          {{ label }} <span v-if="isRequired" class="text-brand-red">*</span>
        </div>
        <div class="w-full flex flex-col gap-y-2">
          <slot name="topAppendage"></slot>
          <div
            class="w-full h-full flex items-center border border-brand-grayblue-300 has-[:focus]:border-primary rounded-sm"
          >
            <div
              :class="[{ 'border-s-4 border-s-primary': isRequired }]"
              class="w-5/12 h-full flex"
            >
              <tippy
                :arrow="false"
                theme="light-border"
                interactive
                placement="bottom-start"
                trigger="focus click"
                class="w-full flex items-center"
                max-width="12rem"
              >
                <div
                  :class="[inputClass, { 'cursor-not-allowed': isPrefixDisabled }]"
                  class="w-full flex gap-x-2 justify-between ps-3 py-0.5"
                  @click.prevent="handleClick($event)"
                >
                  <button
                    :class="{ 'cursor-not-allowed text-gray-400': prefixDisabled }"
                    :disabled="prefixDisabled"
                    class="tippy__trigger text-xs text-start line-clamp-1 truncate"
                    @click.prevent="handleClick($event)"
                  >
                    <span v-if="selectedValue?.label" class="w-full">
                      {{ selectedValue?.label }}
                    </span>
                    <span v-else class="w-full text-gray-400">
                      {{ placeholder || t9nLabel }}
                    </span>
                  </button>
                  <span>
                    <!-- <button @click.prevent="onSelection(null)">
                      <FontAwesomeIcon :icon="faTimes" class="text-brand-black-secondary mx-0.5" />
                    </button> -->
                    <FontAwesomeIcon
                      :icon="faCaretDown"
                      :class="[isPrefixDisabled ? 'text-gray-400' : 'text-brand-black-secondary']"
                      class="mx-0.5"
                    />
                  </span>
                </div>
                <template #content="{ hide }">
                  <div class="tippy-content__container w-full p-0.5">
                    <div class="w-full flex flex-col gap-y-1">
                      <div class="px-1.5 pt-1">
                        <input
                          v-model="searchValue"
                          type="text"
                          class="w-full ps-1 py-1.5 rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary"
                        />
                      </div>
                      <div class="max-h-[12rem] h-full overflow-y-auto">
                        <ul class="divide-y pb-2">
                          <li v-for="(option, key) in filteredOptions" :key="key" class="">
                            <button
                              class="ps-3 py-1.5 hover:bg-gray-100 w-full text-xs text-start line-clamp-1 truncate"
                              @click.prevent="
                                () => {
                                  hide()
                                  onSelection(option)
                                }
                              "
                            >
                              {{ option.label }}
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </template>
              </tippy>
            </div>
            <div class="w-7/12 h-full">
              <input
                type="text"
                class="ps-3 py-1.5 w-full h-full outline-none"
                v-model="textModel"
              />
            </div>
          </div>
          <slot name="bottomAppendage"></slot>
        </div>
      </div>
    </label>
    <div class="flex flex-col gap-y-1 text-xs ltr:font-sans">
      <span :key="err" v-for="err in phoneValidation" class="text-brand-red">
        {{ $t(err) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { faCaretDown } from '@fortawesome/free-solid-svg-icons'
import Fuse from 'fuse.js'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import { computed, defineEmits, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Tippy } from 'vue-tippy'
import HintTippy from '@/components/Form/HintTyppy.vue'

const emit = defineEmits(['onSelection'])

const i18n = useI18n()

const props = defineProps({
  label: String,
  field: Object,
  errors: Array,
  name: String,
  mask: String,
  placeholder: String,
  prefixDisabled: Boolean,
  defaultValue: String,
  maxLength: Number,
  inputType: {
    type: String,
    default: () => 'text'
  },
  inputClass: {
    type: String,
    default: () => ''
  },
  labelClass: String,
  direction: {
    type: String,
    default: 'flex-row md:items-center'
  },
  isRequired: {
    type: Boolean,
    default: () => true
  },
  isPrefixDisabled: Boolean,
  isDisabled: {
    type: Boolean,
    default: false
  },
  options: {
    type: Array,
    required: true
  },
  fuzzyKeywords: {
    type: Array,
    default: () => ['label']
  },
  defaultValue: {
    type: String
  },
  isReadOnly: Boolean
})

const t9nLabel = computed(() => i18n.t(props.label))
const t9nPlaceholder = computed(() => props.placeholder && i18n.t(props.placeholder))

const textModel = ref()
const searchValue = ref()
const wrapperWidth = ref()
const wrapperDomRef = ref()
const selectedValue = ref()
const fuse = ref()

onMounted(() => {
  if (props.field.value) {
    const cleaned = props.field.value.replace('00', '+')
    if (isValidPhoneNumber(cleaned)) {
      const { nationalNumber, countryCallingCode } = parsePhoneNumber(cleaned)
      textModel.value = nationalNumber
      selectedValue.value = parsedOptions.value.find(
        (o) => o.diallingCode == `00${countryCallingCode}`
      )
    } else {
      /**
       * if this point is reached, it means only the dialling code
       * was set as the default value
       */
      selectedValue.value = parsedOptions.value.find(
        (o) => o.diallingCode == props.field.value.replace('+', '00')
      )
    }
  }

  if (props.defaultValue) {
    if (props.defaultValue.startsWith('00971') && props.defaultValue.length > 5) {
      const pattern = /(00971)(.*)/
      const [_, code, phone] = props.defaultValue.match(pattern)
      selectedValue.value = filteredOptions.value.find((o) => o.value == code)
      textModel.value = phone
      return
    }

    selectedValue.value = filteredOptions.value.find((o) => o.diallingCode == props.defaultValue)
    if (props.web?.is_two_way && selectedValue.value) {
      textModel.value = selectedValue.value.value
    }
  }

  fuse.value = new Fuse(parsedOptions.value, {
    includeScore: true,
    threshold: 0.2,
    keys: props.fuzzyKeywords
  })
})

const handleClick = (e) => {
  if (props.prefixDisabled) {
    e.stopPropagation()
    e.preventDefault()
  }
}

onBeforeUnmount(() => {})

/**
 * options with proper labels
 */
const parsedOptions = computed(() => {
  return props.options.map((o) => {
    return {
      ...o,
      label: `${o.diallingCode} - ${o.localizedName}`
    }
  })
})

const filteredOptions = computed(() => {
  if (!searchValue.value) return parsedOptions.value
  const result = fuse.value.search(searchValue.value)
  return result.map((r) => r.item)
})

const fullOutput = computed(() => {
  if (!selectedValue.value || !textModel.value) return null
  const { diallingCode } = selectedValue.value
  return `${diallingCode}${textModel.value}`
})

const setTippyWidth = () => {
  wrapperWidth.value = wrapperDomRef.value.offsetWidth + 'px'
}

const phoneValidation = computed(() => {
  if (!fullOutput.value) return null
  const isValid = isValidPhoneNumber(fullOutput.value.replace('00', '+'))

  if (isValid) {
    return null
  }

  return ['INVALID_PHONE_US']
})

const onSelection = (option) => {
  selectedValue.value = option
  textModel.value = null
  if (!textModel.value) {
    emit('onSelection', null)
  }

  emit('onSelection', fullOutput.value)
}

watch(
  textModel,
  () => {
    if (phoneValidation.value) {
      emit('onSelection', null)
      return
    }
    emit('onSelection', fullOutput.value)
  },
  { immediate: false }
)
</script>

<style lang="scss" scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
